import { useMemo } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { TrendingUp, Target, Globe } from "lucide-react";

// Reads the same lead records the dashboard already has — no extra requests.
// Everything here is first-party data you can quote to a contractor.

const PALETTE = ["#22C55E", "#2B7A78", "#D97736", "#1E3A2B", "#7BA05B", "#C25E3A"];

function tally(records, getKey) {
  const counts = {};
  for (const r of records) {
    const k = getKey(r) || "Unknown";
    counts[k] = (counts[k] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function Panel({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="rounded-2xl border border-forest/12 bg-cream p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-duckgreen/12 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-forest" />
        </div>
        <div className="min-w-0">
          <h3 className="font-serif text-lg font-bold text-forest-950 leading-tight">{title}</h3>
          {subtitle && <p className="text-xs text-forest-950/55 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Ranked({ rows, total, empty }) {
  if (!rows.length) return <p className="text-sm text-forest-950/45 py-2">{empty}</p>;
  return (
    <div className="space-y-2.5">
      {rows.map((row, i) => {
        const pct = total ? Math.round((row.value / total) * 100) : 0;
        return (
          <div key={row.name}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="text-forest-950 font-medium truncate">{row.name}</span>
              <span className="text-forest-950/55 shrink-0 tabular-nums">
                {row.value} · {pct}%
              </span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-pill overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, background: PALETTE[i % PALETTE.length] }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminStats({ records }) {
  const total = records.length;

  const daily = useMemo(() => {
    const days = [];
    const counts = {};
    for (const r of records) {
      const day = (r.received_at || "").slice(0, 10);
      if (day) counts[day] = (counts[day] || 0) + 1;
    }
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push({ day: key.slice(5), leads: counts[key] || 0 });
    }
    return days;
  }, [records]);

  const byPest = useMemo(() => tally(records, (r) => r.pest), [records]);
  const byPage = useMemo(() => tally(records, (r) => r.lead?.page), [records]);
  const bySource = useMemo(
    () => tally(records, (r) => r.lead?.utm_source || (r.lead?.referrer ? "referral" : "direct")),
    [records]
  );

  const delivery = useMemo(() => {
    const delivered = records.filter((r) => r.receipts?.some((x) => x.status === "delivered")).length;
    const sold = records.filter((r) => r.status === "sold").length;
    return {
      delivered,
      rate: total ? Math.round((delivered / total) * 100) : 0,
      sold,
      soldRate: total ? Math.round((sold / total) * 100) : 0,
    };
  }, [records, total]);

  const last30 = daily.reduce((s, d) => s + d.leads, 0);

  if (!total) {
    return (
      <div className="py-20 text-center">
        <TrendingUp className="w-10 h-10 mx-auto text-forest/20" />
        <p className="mt-4 text-forest-950/55">No data yet — stats appear as leads come in.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Panel icon={TrendingUp} title="Leads per day" subtitle={`${last30} in the last 30 days`}>
        <div className="h-44 -ml-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={daily} margin={{ top: 4, right: 4, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E3A2B" opacity={0.08} vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#1E3A2B99" }} interval={6} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#1E3A2B99" }} axisLine={false} tickLine={false} width={28} />
              <Tooltip
                cursor={{ fill: "#1E3A2B0D" }}
                contentStyle={{ borderRadius: 12, border: "1px solid #1E3A2B22", fontSize: 13 }}
              />
              <Bar dataKey="leads" radius={[4, 4, 0, 0]}>
                {daily.map((_, i) => <Cell key={i} fill="#22C55E" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <Panel icon={Target} title="Demand by pest" subtitle="What to sell, and to whom">
        <Ranked rows={byPest} total={total} empty="No pest data." />
      </Panel>

      <div className="grid grid-cols-2 gap-2.5">
        <div className="rounded-2xl border border-forest/12 bg-cream px-4 py-3.5">
          <div className="text-2xl font-bold text-forest-950 leading-none tabular-nums">{delivery.rate}%</div>
          <div className="mt-1.5 text-[11px] uppercase tracking-[0.15em] text-forest-950/55 font-semibold">Delivered</div>
          <div className="mt-1 text-xs text-forest-950/45">{delivery.delivered} of {total}</div>
        </div>
        <div className="rounded-2xl border border-forest/12 bg-cream px-4 py-3.5">
          <div className="text-2xl font-bold text-forest-950 leading-none tabular-nums">{delivery.soldRate}%</div>
          <div className="mt-1.5 text-[11px] uppercase tracking-[0.15em] text-forest-950/55 font-semibold">Sold</div>
          <div className="mt-1 text-xs text-forest-950/45">{delivery.sold} of {total}</div>
        </div>
      </div>

      <Panel icon={Target} title="Converting pages" subtitle="Which landing page produced the lead">
        <Ranked rows={byPage} total={total} empty="No page data." />
      </Panel>

      <Panel icon={Globe} title="Traffic source" subtitle="UTM source, or direct where none was set">
        <Ranked rows={bySource} total={total} empty="No source data." />
      </Panel>
    </div>
  );
}
